import { Vector3, BufferGeometry, Raycaster } from "three";
import * as THREE from "three";
import { DragControls } from "three/addons/controls/DragControls.js";
import { POLYGONS, VARS, VERTICES, LABELS } from "../Global/variables";
import * as Details from "./AreaPitchAzimu";
import { sceneManager } from "../SetUps/SceneManager";
import { updateLabelPositions } from "./Labels";
import { createSegmentPlane } from "../Global/functions";
import { pushUndoAction } from "../Global/UndoManager"; // ⬅️ undo/redo

// Track which vertex is being dragged
let vertexIndex;

// Track the wireframe and its base color during vertex drag
let currentWireframe = null;
let wireframeBaseColor = null;

// Snap distance for 90° vertex drag (very tight, in local/world units of the polygon)
const VERTEX_90_SNAP_DIST = 0.005;

// How close the angle must be to 90° (in degrees) to allow snapping
const ANGLE_SNAP_ENTER_TOL_DEG = 1.0;

// How close to 90° to consider it "highlighted" (green) after snapping
const ANGLE_HIGHLIGHT_TOL_DEG = 0.5;

// DOM element for live angle display while dragging
let angleLabelDiv = null;

// -------------------------------------------------------
// Global drag mode and states
// -------------------------------------------------------

let currentDragMode = null; // null | "vertex" | "translate" | "rotate"

// drag controls for segment vertices, edge-translation handles, and rotation handles
let edgeDragControls = null;
let rotationDragControls = null;

// handles for translating (edge midpoints) and rotating (offset from vertices)
let edgeHandles = [];
let rotationHandles = [];

// state for translation + rotation
// translateState: { segmentIndex, plane, originalPoints, handleStart, moved }
let translateState = null;

// rotationState: { segmentIndex, plane, center, originalPoints, baseDir, moved }
let rotationState = null;

// vertex drag undo state: { segmentIndex, originalPoints, didMove }
let vertexDragState = null;

// remember current polygon details callback so we can update UI from any transform
let currentSetPolygonDetails = null;

// offset distance for rotation handles away from the polygon vertices
const ROTATION_HANDLE_OFFSET = 0.5;

// -------------------------------------------------------
// Angle label helpers (existing)
// -------------------------------------------------------

function ensureAngleLabelDiv() {
  if (!angleLabelDiv) {
    angleLabelDiv = document.createElement("div");
    angleLabelDiv.style.position = "absolute";
    angleLabelDiv.style.pointerEvents = "none";
    angleLabelDiv.style.padding = "2px 4px";
    angleLabelDiv.style.borderRadius = "4px";
    angleLabelDiv.style.fontSize = "11px";
    angleLabelDiv.style.fontFamily = "sans-serif";
    angleLabelDiv.style.background = "rgba(0,0,0,0.7)";
    angleLabelDiv.style.color = "#fff";
    angleLabelDiv.style.zIndex = "9999";
    angleLabelDiv.style.display = "none";
    document.body.appendChild(angleLabelDiv);
  }
}

function updateAngleLabel(angleDeg, worldPos) {
  ensureAngleLabelDiv();

  const camera = sceneManager.camera;
  const renderer = sceneManager.renderer;

  const proj = worldPos.clone().project(camera);
  const width = renderer.domElement.clientWidth;
  const height = renderer.domElement.clientHeight;

  const x = (proj.x * 0.5 + 0.5) * width;
  const y = (-proj.y * 0.5 + 0.5) * height;

  angleLabelDiv.textContent = `${angleDeg.toFixed(1)}°`;
  angleLabelDiv.style.left = `${x + 6}px`;
  angleLabelDiv.style.top = `${y + 6}px`;
  angleLabelDiv.style.display = "block";
}

function hideAngleLabel() {
  if (angleLabelDiv) {
    angleLabelDiv.style.display = "none";
  }
}

function computeAngleDeg(A, V, B) {
  const v1 = A.clone().sub(V);
  const v2 = B.clone().sub(V);
  const len1 = v1.length();
  const len2 = v2.length();
  if (len1 < 1e-6 || len2 < 1e-6) return null;

  const dot = v1.dot(v2) / (len1 * len2);
  const clamped = Math.min(1, Math.max(-1, dot));
  return (Math.acos(clamped) * 180) / Math.PI;
}

/**
 * Given A, B, P, snap P to the "right-angle circle" (Thales) if close enough.
 */
function getVertexRightAngleSnap(A, B, P, maxSnapDist = VERTEX_90_SNAP_DIST) {
  const C = new Vector3().addVectors(A, B).multiplyScalar(0.5);
  const R = A.distanceTo(B) * 0.5;
  if (R <= 1e-6) return null;

  const dir = P.clone().sub(C);
  let len = dir.length();

  if (len < 1e-6) {
    dir.subVectors(B, A);
    len = dir.length();
    if (len < 1e-6) return null;
  }

  dir.multiplyScalar(1 / len);
  const snapped = C.clone().add(dir.multiplyScalar(R));

  const distToSnap = snapped.distanceTo(P);
  return distToSnap <= maxSnapDist ? snapped : null;
}

/**
 * Signed angle between two vectors around a normal.
 */
function signedAngleBetween(v1, v2, normal) {
  const v1n = v1.clone().normalize();
  const v2n = v2.clone().normalize();
  const dot = THREE.MathUtils.clamp(v1n.dot(v2n), -1, 1);
  const angle = Math.acos(dot);
  const cross = new Vector3().crossVectors(v1n, v2n);
  const sign = Math.sign(cross.dot(normal));
  return angle * sign;
}

// -------------------------------------------------------
// Segment helpers
// -------------------------------------------------------

function getPointsCenter(points) {
  const center = new Vector3();
  points.forEach((p) => center.add(p));
  center.divideScalar(points.length || 1);
  return center;
}

/**
 * Helper: get current world-space vertices of a segment from its edges.
 */
function getSegmentCurrentPoints(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.edges) return [];
  return segment.edges.map((e) => e.point1.clone());
}

function createEdgeHandle(position, segmentIndex, edgeIndex) {
  const geo = new THREE.SphereGeometry(0.12, 16, 16);
  const mat = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
  const handle = new THREE.Mesh(geo, mat);
  handle.position.copy(position);
  handle.userData.segmentIndex = segmentIndex;
  handle.userData.edgeIndex = edgeIndex;
  handle.userData.isEdgeHandle = true;

  handle.name = "vertex";
  handle.renderOrder = 1;

  return handle;
}

function createRotationHandle(position, segmentIndex, vertexIndex, baseDir) {
  const geo = new THREE.SphereGeometry(0.1, 16, 16);
  const mat = new THREE.MeshBasicMaterial({ color: 0x00ffff });
  const handle = new THREE.Mesh(geo, mat);
  handle.position.copy(position);
  handle.userData.segmentIndex = segmentIndex;
  handle.userData.vertexIndex = vertexIndex;
  handle.userData.isRotationHandle = true;
  handle.userData.baseDir = baseDir.clone();

  handle.name = "vertex";
  handle.renderOrder = 1;

  return handle;
}

/**
 * Update all measurement labels for a segment after geometry change.
 */
function updateSegmentMeasurementLabels(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !Array.isArray(segment.edges)) return;

  for (const edge of segment.edges) {
    const { point1, point2, id } = edge;
    if (!point1 || !point2 || !id) continue;

    const labelObj = LABELS.measurements[id];
    if (!labelObj) continue;

    const distanceInMeters = point1.distanceTo(point2);
    const totalDistanceInFeet = distanceInMeters * 3.281;
    const totalFeet = Math.floor(totalDistanceInFeet);
    const totalInches = Math.round((totalDistanceInFeet - totalFeet) * 12);

    const currentText = labelObj.element.innerText || "";
    const parts = currentText.split(":");
    let prefix = "";
    if (parts.length > 1) {
      prefix = parts[0] + ": ";
    }

    labelObj.element.innerText = `${prefix}${totalFeet}'${totalInches}"`;
    labelObj.position.lerpVectors(point1, point2, 0.5);
    edge.measurement = totalDistanceInFeet;
  }
}

/**
 * Apply a new set of polygon vertices (world-space) to a segment.
 */
function applySegmentCoordinates(segmentIndex, newPoints) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.shape || !newPoints || !newPoints.length) return;

  const segmentMesh = segment.shape;
  const geom = segmentMesh.geometry;
  const positionAttr = geom.attributes.position;
  const vertexCount = positionAttr.count;

  if (vertexCount !== newPoints.length) {
    console.warn(
      "applySegmentCoordinates: vertex count mismatch",
      vertexCount,
      newPoints.length
    );
  }

  const n = Math.min(vertexCount, newPoints.length);

  // 1) mesh geometry
  for (let i = 0; i < n; i++) {
    const p = newPoints[i];
    positionAttr.setXYZ(i, p.x, p.y, p.z);
  }
  positionAttr.needsUpdate = true;
  geom.computeVertexNormals();
  geom.computeBoundingSphere();

  // 2) VERTICES spheres
  if (VERTICES[segmentIndex]) {
    const verts = VERTICES[segmentIndex];
    for (let i = 0; i < Math.min(verts.length, n); i++) {
      verts[i].position.copy(newPoints[i]);
    }
  }

  // 3) wireframe
  const coordinates = [];
  for (let i = 0; i < n; i++) {
    coordinates.push(newPoints[i].clone());
  }

  const edgePoints = [];
  for (let i = 0; i < coordinates.length; i++) {
    const current = coordinates[i];
    const nextIdx = (i + 1) % coordinates.length;
    const nextPt = coordinates[nextIdx];
    edgePoints.push(current, nextPt);
  }

  const newGeometry = new BufferGeometry().setFromPoints(edgePoints);
  const wireframe = segmentMesh.children.slice(-1)[0];
  if (wireframe && wireframe.geometry && newGeometry.attributes.position) {
    wireframe.geometry.setAttribute("position", newGeometry.attributes.position);
    wireframe.geometry.attributes.position.needsUpdate = true;
  }
  newGeometry.dispose();

  // 4) update POLYGONS[*].edges
  const edges = segment.edges;
  for (let i = 0; i < coordinates.length && i < edges.length; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[(i + 1) % coordinates.length];

    if (!edges[i].point1) edges[i].point1 = p1.clone();
    else edges[i].point1.copy(p1);

    if (!edges[i].point2) edges[i].point2 = p2.clone();
    else edges[i].point2.copy(p2);
  }

  // 5) measurement labels
  updateSegmentMeasurementLabels(segmentIndex);

  // 6) recenter name label
  const center = getPointsCenter(coordinates);
  if (segment.label) {
    segment.label.position.copy(center);
  }

  // 7) update UI details
  if (currentSetPolygonDetails) {
    updateDetails(segmentMesh.uuid, currentSetPolygonDetails);
  }

  // 8) reposition transform handles
  updateTransformHandlesForSegment(segmentIndex, coordinates);
}

/**
 * Update transform handles positions for a segment.
 */
function updateTransformHandlesForSegment(segmentIndex, coordinates) {
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const n = coordinates.length;
  if (n === 0) return;

  if (edgeHandles.length !== n) return;
  if (rotationHandles.length !== n) return;

  const center = getPointsCenter(coordinates);

  // edge handles (midpoints)
  for (let i = 0; i < n; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[(i + 1) % n];
    const mid = new Vector3().addVectors(p1, p2).multiplyScalar(0.5);
    edgeHandles[i].position.copy(mid);
  }

  // rotation handles
  for (let i = 0; i < n; i++) {
    const corner = coordinates[i];
    const dirFromCenter = corner.clone().sub(center);
    let handlePos;
    if (dirFromCenter.lengthSq() > 0.0001) {
      const dirNorm = dirFromCenter.clone().normalize();
      handlePos = corner.clone().add(dirNorm.multiplyScalar(ROTATION_HANDLE_OFFSET));
    } else {
      handlePos = corner.clone().add(new Vector3(0, ROTATION_HANDLE_OFFSET, 0));
    }
    const baseDir = handlePos.clone().sub(center);
    rotationHandles[i].position.copy(handlePos);
    rotationHandles[i].userData.baseDir = baseDir;
  }
}

/**
 * Tear down and dispose all transform handles & DragControls.
 */
function clearTransformHandles() {
  if (edgeDragControls) {
    edgeDragControls.deactivate();
    edgeDragControls.dispose();
    edgeDragControls = null;
  }
  if (rotationDragControls) {
    rotationDragControls.deactivate();
    rotationDragControls.dispose();
    rotationDragControls = null;
  }

  edgeHandles.forEach((h) => {
    if (!h) return;
    if (h.parent) h.parent.remove(h);
    if (h.geometry) h.geometry.dispose();
    if (h.material) h.material.dispose();
  });
  rotationHandles.forEach((h) => {
    if (!h) return;
    if (h.parent) h.parent.remove(h);
    if (h.geometry) h.geometry.dispose();
    if (h.material) h.material.dispose();
  });

  edgeHandles = [];
  rotationHandles = [];
  translateState = null;
  rotationState = null;
}

/**
 * Enables drag controls for a polygon's vertices + whole-segment transform handles.
 */
export const setSegmentDragControls = (index, setPolygonDetails) => {
  currentSetPolygonDetails = setPolygonDetails || null;

  // Remove any existing drag controls + transform handles
  removeSegmentDragControls();
  console.log("Drawing");

  VARS.segDrag = new DragControls(
    VERTICES[index],
    sceneManager.camera,
    sceneManager.renderer.domElement
  );

  VARS.segDrag.addEventListener("hoveron", dragHoverOn);
  VARS.segDrag.addEventListener("hoveroff", dragHoverOff);
  VARS.segDrag.addEventListener("dragend", (event) => dragEnd(event));
  VARS.segDrag.addEventListener("dragstart", (event) => dragStart(event));
  VARS.segDrag.addEventListener("drag", (event) => onDrag(event, setPolygonDetails));

  setupTransformHandlesForSegment(index);

  return VARS.segDrag;
};

function setupTransformHandlesForSegment(segmentIndex) {
  clearTransformHandles();

  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.shape || !segment.edges || !segment.edges.length) return;

  const scene = sceneManager.scene;
  const camera = sceneManager.camera;
  const domElement = sceneManager.renderer.domElement;

  const n = segment.edges.length;
  if (n === 0) return;

  const coords = [];
  for (let i = 0; i < n; i++) {
    const p1 = segment.edges[i].point1;
    coords.push(p1.clone());
  }

  const center = getPointsCenter(coords);

  edgeHandles = [];
  for (let i = 0; i < n; i++) {
    const p1 = coords[i];
    const p2 = coords[(i + 1) % n];
    const mid = new Vector3().addVectors(p1, p2).multiplyScalar(0.5);
    const handle = createEdgeHandle(mid, segmentIndex, i);
    scene.add(handle);
    edgeHandles.push(handle);
  }

  rotationHandles = [];
  for (let i = 0; i < n; i++) {
    const corner = coords[i];
    const dirFromCenter = corner.clone().sub(center);
    let handlePos;
    if (dirFromCenter.lengthSq() > 0.0001) {
      const dirNorm = dirFromCenter.clone().normalize();
      handlePos = corner.clone().add(dirNorm.multiplyScalar(ROTATION_HANDLE_OFFSET));
    } else {
      handlePos = corner.clone().add(new Vector3(0, ROTATION_HANDLE_OFFSET, 0));
    }
    const baseDir = handlePos.clone().sub(center);
    const handle = createRotationHandle(handlePos, segmentIndex, i, baseDir);
    scene.add(handle);
    rotationHandles.push(handle);
  }

  edgeDragControls = new DragControls(edgeHandles, camera, domElement);
  edgeDragControls.addEventListener("dragstart", onEdgeDragStart);
  edgeDragControls.addEventListener("drag", onEdgeDrag);
  edgeDragControls.addEventListener("dragend", onEdgeDragEnd);

  rotationDragControls = new DragControls(rotationHandles, camera, domElement);
  rotationDragControls.addEventListener("dragstart", onRotationDragStart);
  rotationDragControls.addEventListener("drag", onRotationDrag);
  rotationDragControls.addEventListener("dragend", onRotationDragEnd);
}

/**
 * Disposes any existing drag controls and transform handles.
 */
export const removeSegmentDragControls = () => {
  if (VARS.segDrag) {
    VARS.segDrag.dispose();

    VARS.segDrag.removeEventListener("hoveron", dragHoverOn);
    VARS.segDrag.removeEventListener("hoveroff", dragHoverOff);
    VARS.segDrag.removeEventListener("dragend", dragEnd);
    VARS.segDrag.removeEventListener("dragstart", dragStart);
    VARS.segDrag.removeEventListener("drag", onDrag);

    VARS.segDrag = null;
  }

  clearTransformHandles();
  currentDragMode = null;
};

// -------------------------------------------------------
// Existing vertex drag handlers + vertex undo/redo
// -------------------------------------------------------

const dragHoverOn = (event) => {
  event.object.material.opacity = 1;
};

const dragHoverOff = (event) => {
  event.object.material.opacity = 0.6;
};

const dragStart = (event) => {
  if (currentDragMode && currentDragMode !== "vertex") return;
  currentDragMode = "vertex";

  const vertexId = event.object.uuid;
  const segmentMesh = event.object.parent;

  for (let i in segmentMesh.children) {
    if (segmentMesh.children[i].uuid === vertexId) {
      vertexIndex = Number(i);
    }
  }

  // Find segment index in POLYGONS
  const segmentIndex = POLYGONS.findIndex(
    (seg) => seg.shape.uuid === segmentMesh.uuid
  );

  if (segmentIndex !== -1) {
    const segment = POLYGONS[segmentIndex];
    const originalPoints = segment.edges.map((e) => e.point1.clone());
    vertexDragState = {
      segmentIndex,
      originalPoints,
      didMove: false,
    };
  } else {
    vertexDragState = null;
  }

  currentWireframe = segmentMesh.children.slice(-1)[0] || null;
  if (currentWireframe && currentWireframe.material) {
    wireframeBaseColor = currentWireframe.material.color.clone();
  } else {
    wireframeBaseColor = null;
  }

  VARS.isVertexDragActive = true;
  sceneManager.controls.enabled = false;

  ensureAngleLabelDiv();
  angleLabelDiv.style.display = "block";
};

const dragEnd = (event) => {
  if (currentWireframe && wireframeBaseColor) {
    currentWireframe.material.color.copy(wireframeBaseColor);
  }
  currentWireframe = null;
  wireframeBaseColor = null;

  sceneManager.controls.enabled = true;
  VARS.isVertexDragActive = false;

  hideAngleLabel();

  // push undo/redo for vertex move if there was movement
  if (vertexDragState && vertexDragState.didMove) {
    const { segmentIndex, originalPoints } = vertexDragState;
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Move vertex",
      undo: () => {
        applySegmentCoordinates(segmentIndex, originalPoints);
      },
      redo: () => {
        applySegmentCoordinates(segmentIndex, finalPoints);
      },
    });
  }
  vertexDragState = null;

  if (currentDragMode === "vertex") {
    currentDragMode = null;
  }
};

/**
 * Raycast from mouse NDC to model, returns world-space hit.
 */
function getRaycastHitOnModel() {
  const ndc = VARS.mouseNDC;
  const meshes = VARS.Meshes || [];

  if (!ndc || !meshes.length) return null;

  const raycaster = new Raycaster();
  raycaster.setFromCamera({ x: ndc.x, y: ndc.y }, sceneManager.camera);

  const intersects = raycaster.intersectObjects(meshes, true);
  if (!intersects.length) return null;

  return intersects[0].point.clone();
}

const onDrag = (event, setPolygonDetails) => {
  const segmentMesh = event.object.parent;
  const positionAttr = segmentMesh.geometry.attributes.position;
  const vertexCount = positionAttr.count;

  if (typeof vertexIndex !== "number" || vertexCount < 3) {
    return;
  }

  const hitWorld = getRaycastHitOnModel();
  let localPos;
  if (hitWorld) {
    localPos = segmentMesh.worldToLocal(hitWorld.clone());
  } else {
    localPos = event.object.position.clone();
  }

  let snappedPosLocal = localPos.clone();
  let angleDeg = null;

  const prevIndex = (vertexIndex - 1 + vertexCount) % vertexCount;
  const nextIndex = (vertexIndex + 1) % vertexCount;

  const prev = new Vector3(
    positionAttr.getX(prevIndex),
    positionAttr.getY(prevIndex),
    positionAttr.getZ(prevIndex)
  );
  const next = new Vector3(
    positionAttr.getX(nextIndex),
    positionAttr.getY(nextIndex),
    positionAttr.getZ(nextIndex)
  );

  const angleBeforeSnap = computeAngleDeg(prev, localPos, next);
  angleDeg = angleBeforeSnap;

  if (
    angleBeforeSnap != null &&
    Math.abs(angleBeforeSnap - 90) <= ANGLE_SNAP_ENTER_TOL_DEG
  ) {
    const snapCandidate = getVertexRightAngleSnap(
      prev,
      next,
      localPos,
      VERTEX_90_SNAP_DIST
    );
    if (snapCandidate) {
      snappedPosLocal.copy(snapCandidate);
    }
  }

  event.object.position.copy(snappedPosLocal);

  const centerLocal = snappedPosLocal;
  const centerWorld = segmentMesh.localToWorld(centerLocal.clone());

  positionAttr.setXYZ(vertexIndex, centerLocal.x, centerLocal.y, centerLocal.z);
  positionAttr.needsUpdate = true;
  segmentMesh.geometry.computeVertexNormals();
  segmentMesh.geometry.computeBoundingSphere();

  const coordinates = [];
  for (let i = 0; i < positionAttr.count; i++) {
    coordinates.push(
      new Vector3(
        positionAttr.getX(i),
        positionAttr.getY(i),
        positionAttr.getZ(i)
      )
    );
  }

  const edgePoints = [];
  for (let i = 0; i < coordinates.length; i++) {
    const current = coordinates[i];
    const nextIdx = (i + 1) % coordinates.length;
    const nextPt = coordinates[nextIdx];
    edgePoints.push(current, nextPt);
  }

  const newGeometry = new BufferGeometry().setFromPoints(edgePoints);
  const wireframe = segmentMesh.children.slice(-1)[0];
  if (wireframe && wireframe.geometry && newGeometry.attributes.position) {
    wireframe.geometry.setAttribute("position", newGeometry.attributes.position);
    wireframe.geometry.attributes.position.needsUpdate = true;
  }
  newGeometry.dispose();

  const A = coordinates[prevIndex];
  const B = coordinates[nextIndex];
  const angleAfter = computeAngleDeg(A, centerLocal, B);
  if (angleAfter != null) {
    angleDeg = angleAfter;
  }

  const segmentIndex = POLYGONS.findIndex(
    (seg) => seg.shape.uuid === segmentMesh.uuid
  );
  if (segmentIndex !== -1) {
    const segment = POLYGONS[segmentIndex];
    const edges = segment.edges;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const p1 = coordinates[i];
      const p2 = coordinates[(i + 1) % n];

      if (!edges[i].point1) edges[i].point1 = p1.clone();
      else edges[i].point1.copy(p1);

      if (!edges[i].point2) edges[i].point2 = p2.clone();
      else edges[i].point2.copy(p2);
    }

    updateTransformHandlesForSegment(segmentIndex, coordinates);
  }

  const isReallyRightAngle =
    angleDeg != null && Math.abs(angleDeg - 90) <= ANGLE_HIGHLIGHT_TOL_DEG;

  if (isReallyRightAngle && wireframe && wireframe.material) {
    wireframe.material.color.setHex(0x00ff00);
  } else if (wireframe && wireframe.material && wireframeBaseColor) {
    wireframe.material.color.copy(wireframeBaseColor);
  }

  if (angleDeg != null) {
    updateAngleLabel(angleDeg, centerWorld);
  }

  updateLabelPositions(event, centerWorld);
  updateDetails(segmentMesh.uuid, setPolygonDetails);

  // mark that something actually moved for vertex-undo
  if (vertexDragState) {
    vertexDragState.didMove = true;
  }
};

// -------------------------------------------------------
// Segment-level translation via edge handles + undo/redo
// -------------------------------------------------------

function onEdgeDragStart(event) {
  VARS.isVertexDragActive = true;
  if (currentDragMode && currentDragMode !== "translate") return;
  currentDragMode = "translate";

  const handle = event.object;
  const segmentIndex = handle.userData.segmentIndex;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return;

  const originalPoints = segment.edges.map((e) => e.point1.clone());

  const handleStart = new Vector3();
  plane.projectPoint(handle.position, handleStart);

  translateState = {
    segmentIndex,
    plane,
    originalPoints,
    handleStart,
    moved: false, // ⬅️ track if user actually translated
  };

  if (VARS.segDrag) VARS.segDrag.deactivate();
  if (rotationDragControls) rotationDragControls.deactivate();

  if (sceneManager.controls) sceneManager.controls.enabled = false;
}

function onEdgeDrag(event) {
  if (currentDragMode !== "translate" || !translateState) return;

  const { segmentIndex, plane, originalPoints, handleStart } = translateState;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const handle = event.object;

  const projected = new Vector3();
  plane.projectPoint(handle.position, projected);
  handle.position.copy(projected);

  const delta = projected.clone().sub(handleStart);

  const newPoints = originalPoints.map((p) => p.clone().add(delta));
  applySegmentCoordinates(segmentIndex, newPoints);

  // mark as moved if delta is non-zero
  if (delta.lengthSq() > 1e-8) {
    translateState.moved = true;
  }
}

function onEdgeDragEnd(event) {
  VARS.isVertexDragActive = false;
  if (currentDragMode !== "translate" || !translateState) {
    return;
  }

  const { segmentIndex, originalPoints, moved } = translateState;

  currentDragMode = null;
  translateState = null;

  if (sceneManager.controls) sceneManager.controls.enabled = true;

  if (VARS.segDrag) VARS.segDrag.activate();
  if (rotationDragControls) rotationDragControls.activate();

  // push undo/redo if there was an actual translation
  if (moved) {
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Move segment",
      undo: () => {
        applySegmentCoordinates(segmentIndex, originalPoints);
      },
      redo: () => {
        applySegmentCoordinates(segmentIndex, finalPoints);
      },
    });
  }
}

// -------------------------------------------------------
// Segment-level rotation via rotation handles + undo/redo
// -------------------------------------------------------

function onRotationDragStart(event) {
  VARS.isVertexDragActive = true;
  if (currentDragMode && currentDragMode !== "rotate") return;
  currentDragMode = "rotate";

  const handle = event.object;
  const segmentIndex = handle.userData.segmentIndex;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return;

  const originalPoints = segment.edges.map((e) => e.point1.clone());
  const center = getPointsCenter(originalPoints);
  const baseDir = handle.userData.baseDir.clone();

  rotationState = {
    segmentIndex,
    plane,
    center,
    originalPoints,
    baseDir,
    moved: false, // ⬅️ track if rotated
  };

  if (VARS.segDrag) VARS.segDrag.deactivate();
  if (edgeDragControls) edgeDragControls.deactivate();

  if (sceneManager.controls) sceneManager.controls.enabled = false;
}

function onRotationDrag(event) {
  if (currentDragMode !== "rotate" || !rotationState) return;

  const { segmentIndex, plane, center, originalPoints, baseDir } = rotationState;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const handle = event.object;

  const projected = new Vector3();
  plane.projectPoint(handle.position, projected);
  handle.position.copy(projected);

  const currentDir = projected.clone().sub(center);
  if (currentDir.lengthSq() < 1e-8) return;

  const normal = plane.normal.clone().normalize();
  const angle = signedAngleBetween(baseDir, currentDir, normal);

  const newPoints = [];
  for (let i = 0; i < originalPoints.length; i++) {
    const v = originalPoints[i].clone().sub(center);
    v.applyAxisAngle(normal, angle);
    const rotated = center.clone().add(v);
    newPoints.push(rotated);
  }

  applySegmentCoordinates(segmentIndex, newPoints);

  if (Math.abs(angle) > 1e-4) {
    rotationState.moved = true;
  }
}

function onRotationDragEnd(event) {
  if (currentDragMode !== "rotate" || !rotationState) return;

  const { segmentIndex, originalPoints, moved } = rotationState;

  currentDragMode = null;
  rotationState = null;

  VARS.isVertexDragActive = false;
  if (sceneManager.controls) sceneManager.controls.enabled = true;

  if (VARS.segDrag) VARS.segDrag.activate();
  if (edgeDragControls) edgeDragControls.activate();

  // push undo/redo if rotated
  if (moved) {
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Rotate segment",
      undo: () => {
        applySegmentCoordinates(segmentIndex, originalPoints);
      },
      redo: () => {
        applySegmentCoordinates(segmentIndex, finalPoints);
      },
    });
  }
}

// -------------------------------------------------------
// Area/pitch/azimuth update helper
// -------------------------------------------------------

function updateDetails(parentId, setPolygonDetails) {
  for (let segment of POLYGONS) {
    if (segment.shape.uuid === parentId) {
      const lines = segment.edges;

      const res = Details.calculatePitch(lines);
      const pitch = res.pitch_angle;
      const area = Details.getPolyArea(lines).toFixed(2);
      const azimu = Details.getAzimuth(res.p2, res.p1).toFixed(2);

      const data = { pitch, area, azimu };

      setPolygonDetails(data);
      segment.details = data;
      break;
    }
  }
}
